import { css } from '@rxdi/lit-html';

/**
 * Studio reading theme for <markdown-reader>, layered after the reader's
 * base styles: dark surface, readable measure, code in the studio palette.
 * Colours only from shared/themes/studio.css variables.
 */
export const MarkdownStudioStyle = css`
  :host {
    display: block;
    color: var(--rs-text-2);
    font-family: var(--rs-font-body);
    font-size: 18px;
    line-height: 1.75;
  }

  p,
  li {
    color: var(--rs-text-2);
  }

  .content h1,
  .content h2,
  .content h3,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: var(--rs-text);
    font-family: var(--rs-font-display);
    letter-spacing: -0.02em;
    line-height: 1.2;
  }

  .content h1,
  h1 {
    font-size: 34px;
    margin-top: 56px;
  }

  .content h2,
  h2 {
    font-size: 28px;
    margin-top: 48px;
  }

  .content h3,
  h3 {
    font-size: 22px;
    margin-top: 36px;
  }

  a {
    color: var(--rs-signal);
    text-decoration: underline;
    text-decoration-color: var(--rs-signal-soft);
    text-underline-offset: 3px;
  }

  a:hover {
    text-decoration-color: var(--rs-signal);
  }

  strong {
    color: var(--rs-text);
  }

  /* inline code */
  code,
  p code {
    font-family: var(--rs-font-mono);
    font-size: 0.86em;
    color: var(--rs-ion) !important;
    background: var(--rs-ion-soft);
    border-radius: 6px;
  }

  /* code blocks */
  pre[class*='language-'],
  :not(pre) > code[class*='language-'] {
    background: var(--rs-code-bg);
    border: 1px solid var(--rs-line);
    border-radius: 14px;
    box-shadow: none;
  }

  pre {
    box-shadow: none;
  }

  code[class*='language-'],
  pre[class*='language-'],
  pre code[class*='language-sh'] {
    color: var(--rs-text) !important;
    background: none;
    font-family: var(--rs-font-mono);
    font-size: 14px !important;
    line-height: 1.7 !important;
  }

  .graphql {
    background: var(--rs-code-bg) !important;
    border-radius: 14px !important;
  }

  .code-label,
  .filename,
  h6 {
    background: var(--rs-surface-2);
    color: var(--rs-text-muted);
    font-family: var(--rs-font-mono);
    font-size: 12px;
    border: 1px solid var(--rs-line);
    border-bottom: none;
    border-radius: 14px 14px 0 0;
  }

  div.prism-show-language > div.prism-show-language-label {
    background: none;
    color: var(--rs-signal);
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .code-label + pre[class*='language-'] {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  pre[class*='language-sh'] {
    background: var(--rs-code-bg);
  }

  .token.comment,
  .token.prolog,
  .token.doctype,
  .token.cdata {
    color: var(--rs-code-comment);
  }

  .token.punctuation {
    color: var(--rs-text-muted);
  }

  .token.property,
  .token.tag,
  .token.boolean,
  .token.number,
  .token.constant,
  .token.symbol {
    color: var(--rs-code-dec);
  }

  .token.selector,
  .token.attr-name,
  .token.string,
  .token.char,
  .token.builtin,
  .token.inserted {
    color: var(--rs-code-str);
  }

  .token.entity,
  .token.url,
  .token.variable {
    color: var(--rs-text);
  }

  .token.function,
  .token.operator {
    color: var(--rs-code-fn);
  }

  .token.atrule,
  .token.attr-value,
  .token.keyword {
    color: var(--rs-code-key);
  }

  /* lists - the base theme draws a white panel with a teal bar */
  ul,
  ol {
    margin: 20px 0;
    padding: 0 0 0 24px;
    background: none;
    border: none;
  }

  ul > li > ul,
  ol > li > ol,
  ul > li > ol,
  ol > li > ul {
    margin: 8px 0;
  }

  li {
    padding: 0 0 6px 4px;
    color: var(--rs-text-2);
  }

  li::marker {
    color: var(--rs-signal);
  }

  ol > li::marker {
    font-family: var(--rs-font-mono);
    font-size: 0.85em;
  }

  /* file trees and external-link boxes */
  .file-tree,
  .external {
    background: var(--rs-surface);
    border: 1px solid var(--rs-line);
    border-radius: 12px;
    color: var(--rs-text-2);
  }

  .file-tree .item {
    color: var(--rs-text-2);
    border-color: var(--rs-line-strong);
  }

  /* quotes */
  blockquote,
  blockquote.warning,
  blockquote.info {
    background: var(--rs-surface);
    border-radius: 0 12px 12px 0;
    color: var(--rs-text-2);
  }

  blockquote::before,
  blockquote.info::before {
    background: var(--rs-signal);
  }

  blockquote strong,
  blockquote.info strong,
  blockquote.info a {
    color: var(--rs-signal);
  }

  blockquote.warning strong,
  blockquote.warning a {
    color: var(--rs-ember);
  }

  hr {
    height: 1px;
    background: var(--rs-line-strong);
  }

  /* tables */
  th {
    background: var(--rs-surface-2);
    color: var(--rs-text);
  }

  tr:nth-child(even) {
    background: var(--rs-surface);
  }

  tr td {
    border-bottom: 1px solid var(--rs-line);
  }

  tr:nth-of-type(even) td {
    background: var(--rs-surface);
  }

  tr:nth-of-type(odd) td:first-of-type,
  tr:nth-of-type(even) td:first-of-type {
    border-right: 1px solid var(--rs-line);
  }

  figure img,
  img {
    max-width: 100%;
    border-radius: 14px;
    border: 1px solid var(--rs-line);
  }

  figcaption {
    color: var(--rs-text-muted);
  }

  ::selection {
    background: var(--rs-signal);
    color: var(--rs-text-on-accent);
  }
`;
